/**
 * BarChart Component
 *
 * Reusable bar chart wrapper with:
 * - Vertical or horizontal orientation
 * - Multiple bar support
 * - Customizable colors
 * - Loading skeleton
 * - Card wrapper with title
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'

export interface BarChartProps {
  title: string
  data: Array<Record<string, any>>
  dataKeys: Array<{
    key: string
    label: string
    color?: string
  }>
  xAxisKey?: string
  loading?: boolean
  height?: number
  orientation?: 'vertical' | 'horizontal'
  className?: string
}

const DEFAULT_COLORS = [
  'hsl(var(--chart-1))',
  'hsl(var(--chart-2))',
  'hsl(var(--chart-3))',
  'hsl(var(--chart-4))',
  'hsl(var(--chart-5))',
]

export function BarChart({
  title,
  data,
  dataKeys,
  xAxisKey = 'name',
  loading = false,
  height = 300,
  orientation = 'vertical',
  className,
}: BarChartProps) {
  if (loading) {
    return (
      <Card className={className}>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent>
          <Skeleton className="w-full" style={{ height: `${height}px` }} />
        </CardContent>
      </Card>
    )
  }

  if (!data || data.length === 0) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div
            className="flex items-center justify-center text-muted-foreground"
            style={{ height: `${height}px` }}
          >
            No data available
          </div>
        </CardContent>
      </Card>
    )
  }

  // Build chart config for tooltips
  const chartConfig: Record<string, { label: string; color: string }> = {}
  dataKeys.forEach((dataKey, index) => {
    chartConfig[dataKey.key] = {
      label: dataKey.label,
      color: dataKey.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length],
    }
  })

  // Debug logging
  console.log('BarChart render:', {
    title,
    dataLength: data.length,
    dataItems: data,
    xAxisKey,
    dataKeys,
  })

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div style={{ width: '100%', height: `${height}px` }}>
          <ResponsiveContainer width="100%" height="100%">
            <RechartsBarChart
              data={data}
              layout={orientation === 'horizontal' ? 'horizontal' : 'vertical'}
              margin={{ top: 5, right: 30, left: 20, bottom: 60 }}
              barCategoryGap="20%"
            >
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              {orientation === 'vertical' ? (
                <>
                  <XAxis
                    dataKey={xAxisKey}
                    tick={{ fontSize: 12 }}
                    tickLine={false}
                    axisLine={false}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    tickLine={false}
                    axisLine={false}
                  />
                </>
              ) : (
                <>
                  <XAxis
                    type="number"
                    tick={{ fontSize: 12 }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    type="category"
                    dataKey={xAxisKey}
                    tick={{ fontSize: 12 }}
                    tickLine={false}
                    axisLine={false}
                  />
                </>
              )}
              <Tooltip
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null

                  return (
                    <div className="rounded-lg border bg-background p-2 shadow-sm">
                      <div className="grid gap-2">
                        {payload.map((entry: any) => (
                          <div key={entry.dataKey} className="flex items-center gap-2">
                            <div
                              className="h-2.5 w-2.5 rounded-full"
                              style={{ backgroundColor: entry.color }}
                            />
                            <span className="text-sm text-muted-foreground">
                              {chartConfig[entry.dataKey]?.label}:
                            </span>
                            <span className="text-sm font-medium">
                              {typeof entry.value === 'number'
                                ? entry.value.toLocaleString()
                                : entry.value}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                }}
              />
              <Legend />
              {dataKeys.map((dataKey, index) => (
                <Bar
                  key={dataKey.key}
                  dataKey={dataKey.key}
                  fill={dataKey.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length]}
                  radius={[4, 4, 0, 0]}
                  maxBarSize={100}
                />
              ))}
            </RechartsBarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  )
}
